import type {AreaEffect} from "@/encounter/model/AreaEffect.ts";
import type Combatant from "@/encounter/model/Combatant.ts";
import type Position from "@/util/Position.ts";
import {PIXELS_PER_METER} from "@/dungeons/geometry/Coordinates.ts";
import {toAreaEffectLocalPosition} from "./AreaEffectGeometry.ts";

/** A token is affected when its center lies inside the rendered effect shape. */
export function pointInAreaEffect(
    effect: AreaEffect,
    point: Position,
    source?: Pick<Combatant, "position" | "rotation">,
): boolean {
    const origin = source?.position ?? effect.position;
    const rotation = source?.rotation ?? effect.rotation ?? 0;
    const local = toAreaEffectLocalPosition(point, origin, rotation);

    switch (effect.type) {
        case "Rect": {
            const length = (effect.length ?? 0) * PIXELS_PER_METER;
            const halfWidth = (effect.width ?? 0) * PIXELS_PER_METER / 2;
            return length > 0 && halfWidth > 0
                && local.x >= 0 && local.x <= length && Math.abs(local.y) <= halfWidth;
        }
        case "Sphere": {
            const radius = (effect.radius ?? 0) * PIXELS_PER_METER;
            return radius > 0 && Math.hypot(local.x, local.y) <= radius;
        }
        case "Arc": {
            const radius = (effect.radius ?? 0) * PIXELS_PER_METER;
            const width = (effect.width ?? 0) * PIXELS_PER_METER;
            const angle = effect.angle ?? 0;
            if (radius <= 0 || width <= 0 || angle <= 0) return false;
            const distance = Math.hypot(local.x, local.y);
            if (distance < Math.max(0, radius - width) || distance > radius) return false;
            return angle >= 360 || Math.abs(Math.atan2(local.y, local.x) * 180 / Math.PI) <= angle / 2;
        }
    }
}
